import React, { useMemo, useCallback, useState } from 'react';
import {
    useTable,
    useBlockLayout,
    HeaderGroup,
    useSortBy,
    Row,
} from 'react-table';
import { FixedSizeList } from 'react-window';

import {
    DateTimeCell,
    LoadingSpinner,
    TableHeader,
    TableHeaderGroup,
    TableRow,
    TimeCodeCell,
    TruncatedTwoRowCell,
} from '@trqk/components';
import { formatTableString, getScrollbarWidth } from '@trqk/utils';

import {
    columnWidths,
    defaultColumnWidth,
    rowHeight,
    tableHeight,
} from './constants';

interface WorkDetailDetectionsTableProps {
    detections: BmatDetection[];
    loading: boolean;
}

/* eslint-disable react/jsx-props-no-spreading */

export const WorkDetailDetectionsTable = ({
    detections,
    loading,
}: WorkDetailDetectionsTableProps): JSX.Element => {
    /* BEGIN UI state logic */

    // We do not reset the data in the table unless the array length is different. This
    // means we have to trigger a re-fetch of the whole list manually from the parent if we want
    // to confidently update all the data at once.
    const [data, setData] = useState(detections);

    if (detections.length !== data.length) {
        setData(detections);
    }

    /* END UI state logic */

    /* BEGIN table management logic */

    const defaultColumn = useMemo(
        () => ({
            width: defaultColumnWidth,
        }),
        [],
    );

    const columns = useMemo(
        () => [
            {
                id: 'timestamp',
                accessor: ({ timestamp }: BmatDetection): Maybe<string> =>
                    timestamp,
                Cell: DateTimeCell,
                headerLabel: 'Date & Time (UTC)',
                Header: TableHeader,
                width: columnWidths.timestamp,
                sortType: (
                    rowA: Row<BmatDetection>,
                    rowB: Row<BmatDetection>,
                ): number => {
                    const detectionStartA = new Date(
                        rowA?.original?.timestamp ?? 0,
                    );
                    const detectionStartB = new Date(
                        rowB?.original?.timestamp ?? 0,
                    );

                    if (detectionStartA < detectionStartB) {
                        return 1;
                    }

                    if (detectionStartA > detectionStartB) {
                        return -1;
                    }

                    return 0;
                },
            },
            {
                id: 'duration',
                accessor: ({ duration }: BmatDetection): Maybe<number> =>
                    duration ?? null,
                Cell: TimeCodeCell,
                headerLabel: 'Duration',
                Header: TableHeader,
                width: columnWidths.duration,
            },
            {
                id: 'station',
                accessor: ({ channel }: BmatDetection): Maybe<string> =>
                    channel ?? null,
                Cell: TruncatedTwoRowCell,
                tooltipPosition: 'bottom',
                headerLabel: 'Station',
                Header: TableHeader,
                width: columnWidths.station,
            },
            {
                id: 'show',
                accessor: ({ show }: BmatDetection): string =>
                    formatTableString(show ?? undefined),
                Header: TableHeader,
                headerLabel: 'Show',
                Cell: TruncatedTwoRowCell,
                width: columnWidths.show,
                tooltipPosition: 'left',
            },
            {
                id: 'episode',
                accessor: ({
                    episode_number,
                    episode,
                }: BmatDetection): string => {
                    if (
                        (episode_number?.length ?? 0) > 0 &&
                        (episode?.length ?? 0) > 0
                    ) {
                        return formatTableString(
                            `EP ${episode_number}: ${episode}`,
                        );
                    }

                    return formatTableString(episode ?? undefined);
                },
                Header: TableHeader,
                headerLabel: 'Episode',
                Cell: TruncatedTwoRowCell,
                width: columnWidths.episode,
                tooltipPosition: 'left',
            },
        ],
        [],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        totalColumnsWidth,
        prepareRow,
    } = useTable<BmatDetection>(
        {
            columns,
            data,
            defaultColumn,
            initialState: {
                sortBy: [
                    {
                        id: 'detection_start',
                        desc: false,
                    },
                ],
            },
        },
        useBlockLayout,
        useSortBy,
    );

    /* END table management logic */
    const RenderRow = useCallback(
        ({ index, style }) => {
            const row = rows[index];

            prepareRow(row);

            return (
                <TableRow<BmatDetection>
                    index={index}
                    style={style}
                    row={row}
                />
            );
        },
        [prepareRow, rows],
    );

    const scrollbarWidth = useMemo(() => getScrollbarWidth(), []);

    return (
        <div {...getTableProps()} className="trqk-table text-sm font-normal">
            <div>
                {headerGroups.map(
                    (headerGroup: HeaderGroup<BmatDetection>): JSX.Element => {
                        const { key: headerGroupKey } =
                            headerGroup.getHeaderGroupProps();

                        return (
                            <TableHeaderGroup<BmatDetection>
                                key={headerGroupKey}
                                headerGroup={headerGroup}
                            />
                        );
                    },
                )}
            </div>
            <div {...getTableBodyProps()}>
                {loading && (
                    <div
                        style={{
                            height: tableHeight,
                        }}
                        className="flex w-full items-center justify-center"
                    >
                        <LoadingSpinner />
                    </div>
                )}
                {!loading && (
                    <FixedSizeList
                        height={tableHeight}
                        itemCount={rows.length}
                        itemSize={rowHeight}
                        width={totalColumnsWidth + scrollbarWidth}
                        style={{
                            overflowX: 'hidden',
                        }}
                    >
                        {RenderRow}
                    </FixedSizeList>
                )}
            </div>
        </div>
    );
};
